import api from 'api/apiClient';

/**
 * The people-record side of HR (`HRPeopleController`): leave entitlement, the employee
 * document file, letters & certificates, and offboarding.
 */

const unwrap = async <T>(promise: Promise<{ data: T }>): Promise<T> => (await promise).data;

const toArray = <T>(body: any): T[] => {
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  return [];
};

const payload = async <T>(promise: Promise<{ data: any }>): Promise<T> =>
  (await promise).data?.data as T;

/* ── Leave entitlement ───────────────────────────────────────────────────── */

export interface LeaveTypeRow {
  id: number;
  code: string;
  label: string;
  annual_quota: number;
  is_paid: boolean;
  requires_document: boolean;
  is_active: boolean;
}

export interface LeaveBalanceRow {
  leave_type_id: number;
  code: string;
  label: string;
  is_paid: boolean;
  requires_document: boolean;
  /** False for types with no quota — leave without pay is always available. */
  tracked: boolean;
  entitled: number | null;
  carried_forward: number;
  taken: number;
  pending: number;
  available: number | null;
}

export async function getLeaveTypes(): Promise<LeaveTypeRow[]> {
  return toArray<LeaveTypeRow>(await unwrap(api.get('/api/hr/leave-types')));
}

export async function updateLeaveType(
  leaveTypeId: number,
  body: Partial<LeaveTypeRow>
): Promise<LeaveTypeRow> {
  return payload<LeaveTypeRow>(api.put(`/api/hr/leave-types/${leaveTypeId}`, body));
}

export async function getLeaveBalance(employeeId: number | string): Promise<LeaveBalanceRow[]> {
  return toArray<LeaveBalanceRow>(
    await unwrap(api.get(`/api/hr/employee/${employeeId}/leave-balance`))
  );
}

/** The employee's own balance — the same figures through the self-service route. */
export async function getMyLeaveBalance(employeeId: number | string): Promise<LeaveBalanceRow[]> {
  return toArray<LeaveBalanceRow>(
    await unwrap(api.get(`/api/employee/${employeeId}/leave-balance`))
  );
}

export async function setLeaveEntitlement(
  employeeId: number | string,
  body: { leave_type_id: number; entitled_days?: number; carried_forward?: number; notes?: string }
): Promise<LeaveBalanceRow[]> {
  return payload<LeaveBalanceRow[]>(
    api.put(`/api/hr/employee/${employeeId}/leave-entitlement`, body)
  );
}

/* ── Employee documents ──────────────────────────────────────────────────── */

export interface EmployeeDocumentRow {
  document_type: string;
  label: string;
  id: number | null;
  file_id: number | null;
  url: string | null;
  file_name: string | null;
  issued_on: string | null;
  expires_on: string | null;
  notes: string | null;
  uploaded_on: string | null;
  expired: boolean;
}

export interface DocumentChecklist {
  documents: EmployeeDocumentRow[];
  present: number;
  expected: number;
}

export async function getEmployeeDocuments(employeeId: number | string): Promise<DocumentChecklist> {
  return (
    (await payload<DocumentChecklist>(api.get(`/api/hr/employee/${employeeId}/documents`))) ?? {
      documents: [],
      present: 0,
      expected: 0,
    }
  );
}

export async function uploadEmployeeDocument(
  employeeId: number | string,
  body: {
    document_type: string;
    file_id: number | string;
    label?: string;
    issued_on?: string | null;
    expires_on?: string | null;
    notes?: string | null;
  }
): Promise<EmployeeDocumentRow> {
  return payload<EmployeeDocumentRow>(api.post(`/api/hr/employee/${employeeId}/documents`, body));
}

export async function deleteEmployeeDocument(
  employeeId: number | string,
  documentId: number
): Promise<void> {
  await unwrap(api.delete(`/api/hr/employee/${employeeId}/documents/${documentId}`));
}

/* ── Letters & certificates ──────────────────────────────────────────────── */

export interface LetterTemplate {
  code: string;
  label: string;
  subject: string;
  /** Extra free-text fields this template asks the issuer for. */
  prompts: string[];
}

export interface LetterRow {
  id: number;
  employee_id: number;
  employee_name: string | null;
  employee_code: string | null;
  department: string | null;
  letter_type: string;
  letter_label: string;
  reference_no: string;
  subject: string;
  body: string;
  issued_on: string;
  issued_by: string | null;
  url: string | null;
}

export async function getLetterTemplates(): Promise<LetterTemplate[]> {
  return toArray<LetterTemplate>(await unwrap(api.get('/api/hr/letter-templates')));
}

export async function getLetters(employeeId?: number | string): Promise<LetterRow[]> {
  const query = employeeId ? `?employeeId=${employeeId}` : '';
  return toArray<LetterRow>(await unwrap(api.get(`/api/hr/letters${query}`)));
}

/** Renders without issuing, so the text can be read (and edited) before it gets a reference. */
export async function previewLetter(body: Record<string, unknown>): Promise<{
  letter_type: string;
  subject: string;
  body: string;
}> {
  return payload(api.post('/api/hr/letters/preview', body));
}

export async function issueLetter(body: Record<string, unknown>): Promise<LetterRow> {
  return payload<LetterRow>(api.post('/api/hr/letters', body));
}

export async function deleteLetter(letterId: number): Promise<void> {
  await unwrap(api.delete(`/api/hr/letters/${letterId}`));
}

/* ── Offboarding ─────────────────────────────────────────────────────────── */

export interface EmployeeExit {
  id: number;
  employee_id: number;
  employee_name: string | null;
  department: string | null;
  exit_type: string;
  resignation_date: string | null;
  last_working_day: string | null;
  notice_days: number | null;
  reason: string | null;
  exit_interview_done: boolean;
  assets_returned: boolean;
  knowledge_transfer_done: boolean;
  dues_cleared: boolean;
  clearance_complete: boolean;
  fnf_amount: number | null;
  fnf_paid_on: string | null;
  remarks: string | null;
  completed_at: string | null;
}

export async function getEmployeeExit(employeeId: number | string): Promise<EmployeeExit | null> {
  return payload<EmployeeExit | null>(api.get(`/api/hr/employee/${employeeId}/exit`));
}

export async function saveEmployeeExit(
  employeeId: number | string,
  body: Partial<EmployeeExit>
): Promise<EmployeeExit> {
  return payload<EmployeeExit>(api.put(`/api/hr/employee/${employeeId}/exit`, body));
}

export async function completeEmployeeExit(employeeId: number | string): Promise<EmployeeExit> {
  return payload<EmployeeExit>(api.post(`/api/hr/employee/${employeeId}/exit/complete`, {}));
}

export async function getOpenExits(): Promise<EmployeeExit[]> {
  return toArray<EmployeeExit>(await unwrap(api.get('/api/hr/exits')));
}

